import { useEffect, useMemo, useState } from "react";
import { MapPin, BedDouble, Bath, Maximize2, ArrowUpRight, Flame, CalendarCheck2, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { fmtPrice, imgUrl } from "../lib/format";
import { requestPropertyVisit } from "../lib/visit";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200";
const PAGE_SIZE = 12;

function requestVisit(property) {
  requestPropertyVisit(property, api);
}

const TABS = [
  { key: "Tous", labelKey: "filters.all" },
  { key: "Vente", labelKey: "filters.for_sale" },
  { key: "Location", labelKey: "filters.for_rent" },
];

export default function Properties({ filter, onOpen, showAll = false, title, subtitle, hideHeader = false }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("Tous");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const {
    transaction: fTx,
    type: fType,
    location: fLoc,
    min_price: fMinPrice,
    max_price: fMaxPrice,
    bedrooms_min: fBedroomsMin,
    min_surface: fMinSurface,
    max_surface: fMaxSurface,
  } = filter || {};

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (!showAll) params.featured = true;
    if (fTx) params.transaction = fTx;
    if (fType) params.type = fType;
    if (fLoc) params.location = fLoc;
    if (fMinPrice !== undefined) params.min_price = fMinPrice;
    if (fMaxPrice !== undefined) params.max_price = fMaxPrice;
    if (fBedroomsMin !== undefined) params.bedrooms_min = fBedroomsMin;
    if (fMinSurface !== undefined) params.min_surface = fMinSurface;
    if (fMaxSurface !== undefined) params.max_surface = fMaxSurface;
    api.properties(params)
      .then((data) => {
        if (cancelled) return;
        const sorted = [...data].sort((a, b) => {
          if (a.featured !== b.featured) return a.featured ? -1 : 1;
          if (a.featured && b.featured) {
            const ao = Number.isInteger(a.featured_order) ? a.featured_order : 9999;
            const bo = Number.isInteger(b.featured_order) ? b.featured_order : 9999;
            if (ao !== bo) return ao - bo;
          }
          return (b.created_at || "").localeCompare(a.created_at || "");
        });
        setItems(sorted);
        setPage(1);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [fTx, fType, fLoc, fMinPrice, fMaxPrice, fBedroomsMin, fMinSurface, fMaxSurface, showAll]);

  const filtered = useMemo(() => {
    if (tab === "Tous") return items;
    return items.filter((p) => p.transaction === tab);
  }, [items, tab]);

  // Pagination only on the "all" listing; featured stays as-is
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = showAll
    ? filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)
    : filtered;

  // Reset to page 1 if user switches the Tous/Vente/Location tab
  useEffect(() => { setPage(1); }, [tab]);

  return (
    <section id="biens" className="py-20 md:py-28 relative" data-testid="properties-section">
      {/* Soft section background tint */}
      <div className="absolute inset-0 bg-[#F2F0EB]/60 pointer-events-none" aria-hidden="true" />
      <div className="relative mx-auto max-w-[1480px] px-4 sm:px-6 md:px-12 lg:px-20">
        {!hideHeader && (
          <div className="mb-12 md:mb-16">
            <div className="grid grid-cols-12 gap-6 items-end">
              <div className="col-span-12 lg:col-span-7">
                <div className="flex items-center gap-3 mb-4">
                  <span className="hairline" />
                  <span className="overline">{subtitle || t(showAll ? "properties.all_subtitle" : "properties.section_overline")}</span>
                </div>
                <h2 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[76px] leading-[0.97] tracking-[-0.022em] font-medium">
                  {title || t(showAll ? "properties.all_title" : "properties.featured_title")}
                </h2>
              </div>
              <div className="col-span-12 lg:col-span-5 flex flex-wrap items-center gap-4 lg:justify-end">
                <div className="flex items-center gap-1 border border-[#E5E0D8] p-1 bg-white" data-testid="property-tabs">
                  {TABS.map((tb) => (
                    <button
                      key={tb.key}
                      data-testid={`tab-${tb.key.toLowerCase()}`}
                      onClick={() => setTab(tb.key)}
                      className={`px-5 py-2 text-[12px] tracking-[0.2em] uppercase font-semibold transition-colors ${
                        tab === tb.key ? "bg-[#1A1A1A] text-white" : "text-[#1A1A1A] hover:bg-[#F9F8F6]"
                      }`}
                    >
                      {t(`properties.${tb.labelKey}`)}
                    </button>
                  ))}
                </div>
                {!showAll && (
                  <a
                    href="/biens"
                    className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-semibold text-[#1A1A1A] hover:text-[#A3805B] border-b-2 border-[#1A1A1A] hover:border-[#A3805B] pb-1 transition-colors"
                    data-testid="properties-see-all"
                  >
                    Voir tous les biens
                    <ChevronRight size={14} strokeWidth={1.8} />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {showAll && !loading && filtered.length > 0 && (
          <div
            className="flex items-center justify-between gap-4 mb-6 text-[12px] tracking-[0.18em] uppercase text-[#6B6B6B]"
            data-testid="properties-results-count"
          >
            <span>
              {filtered.length <= 1
                ? t("properties.filters.results_count_one", { count: filtered.length })
                : t("properties.filters.results_count_other", { count: filtered.length })}
            </span>
            {totalPages > 1 && (
              <span data-testid="properties-page-indicator">
                {t("properties.filters.page_of", { page: safePage, total: totalPages })}
              </span>
            )}
          </div>
        )}

        {renderList(loading, pageItems, onOpen, t, showAll)}

        {showAll && totalPages > 1 && !loading && (
          <div className="flex items-center justify-center gap-4 mt-12" data-testid="properties-pagination">
            <button
              type="button"
              onClick={() => { setPage((p) => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              disabled={safePage <= 1}
              className="inline-flex items-center gap-2 border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors"
              data-testid="page-prev"
            >
              <ChevronLeft size={14} strokeWidth={1.6} />
              {t("properties.filters.prev")}
            </button>
            <span className="text-[12px] tracking-[0.18em] uppercase text-[#6B6B6B]">
              {t("properties.filters.page_of", { page: safePage, total: totalPages })}
            </span>
            <button
              type="button"
              onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              disabled={safePage >= totalPages}
              className="inline-flex items-center gap-2 border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors"
              data-testid="page-next"
            >
              {t("properties.filters.next")}
              <ChevronRight size={14} strokeWidth={1.6} />
            </button>
          </div>
        )}

        <p className="text-xs text-[#6B6B6B] italic mt-8 max-w-[900px]" data-testid="properties-disclaimer">
          Photographies, surfaces, plans et descriptions non contractuels — informations fournies à
          titre indicatif. Seul le compromis de vente ou le bail signé fait foi.
        </p>
      </div>
    </section>
  );
}

function renderList(loading, items, onOpen, t, showAll = false) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
        {["s1", "s2", "s3"].map((k) => (
          <div key={k} className="aspect-[4/3] bg-[#EFEBE3] animate-pulse rounded-[24px]" />
        ))}
      </div>
    );
  }
  if (items.length === 0) {
    return <div className="text-center py-16 text-[#6B6B6B]">{t("properties.no_results")}</div>;
  }
  // Home (featured): max 3 properties side by side. /biens (showAll): full grid.
  const list = showAll ? items : items.slice(0, 3);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
      {list.map((p, idx) => (
        <PropertyCard key={p.id} property={p} onClick={() => onOpen(p)} t={t} />
      ))}
    </div>
  );
}

function PropertyCardImages({ images, title, active }) {
  const list = (images && images.length) ? images : [null];
  const count = list.length;
  const [idx, setIdx] = useState(0);

  // Photos rotate ONLY while the visitor hovers the card — calm by default,
  // engaging when a listing catches their eye.
  useEffect(() => {
    if (count <= 1 || !active) return;
    const intervalId = setInterval(() => setIdx((i) => (i + 1) % count), 2200);
    return () => clearInterval(intervalId);
  }, [count, active]);

  return (
    <>
      {list.map((src, i) => {
        const url = src ? (imgUrl(src, 1000) || src) : FALLBACK_IMG;
        const srcSet = src
          ? `${imgUrl(src, 480)} 480w, ${imgUrl(src, 800)} 800w, ${imgUrl(src, 1200)} 1200w, ${imgUrl(src, 1600)} 1600w`
          : undefined;
        return (
          <img
            key={src || `placeholder-${i}`}
            src={url}
            srcSet={srcSet}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            alt={`${title} — photo ${i + 1}`}
            loading={i === 0 ? "eager" : "lazy"}
            decoding="async"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1100ms] ease-in-out ${
              i === idx ? "opacity-100" : "opacity-0"
            }`}
          />
        );
      })}
      {count > 1 && (
        <div
          className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10 px-2 py-1 rounded-full bg-black/25 backdrop-blur-[3px]"
          data-testid={`card-carousel-dots`}
        >
          {list.map((src, i) => (
            <button
              key={`${src || "placeholder"}-${i}`}
              type="button"
              onClick={(e) => { e.stopPropagation(); setIdx(i); }}
              aria-label={`Photo ${i + 1}`}
              className={`h-[5px] rounded-full transition-all duration-300 ${
                i === idx ? "w-5 bg-white" : "w-[5px] bg-white/65 hover:bg-white/90"
              }`}
            />
          ))}
        </div>
      )}
    </>
  );
}

function PropertyCard({ property: p, onClick, t }) {
  const showSpecs = p.type !== "Terrain" && p.type !== "Commercial";
  const [hover, setHover] = useState(false);
  return (
    <article
      data-testid={`property-card-${p.id}`}
      className="prop-card group cursor-pointer h-full flex flex-col"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="relative overflow-hidden bg-[#EFEBE3] rounded-[24px] prop-card-img-wrap shadow-[0_1px_2px_rgba(0,0,0,0.04)] group-hover:shadow-[0_24px_50px_-12px_rgba(26,26,26,0.32)] transition-shadow duration-500 aspect-[4/3]">
        <PropertyCardImages images={p.images} title={p.title} active={hover} />
        {p.images?.length > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/65 text-white px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase font-semibold rounded-full z-10">
            {p.images.length} photos
          </div>
        )}
        <div className="absolute top-4 left-4 flex flex-col gap-1.5 z-10">
          <div className="bg-white/95 backdrop-blur-[2px] px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase font-semibold rounded-full shadow-sm">
            {p.transaction}
          </div>
          {p.featured && (
            <div className="bg-[#A3805B] text-white px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase font-semibold rounded-full shadow-sm">
              ★ Vedette
            </div>
          )}
          {p.trending && (
            <div
              className="bg-gradient-to-r from-[#E25822] to-[#A3805B] text-white px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase font-semibold flex items-center gap-1 rounded-full shadow-sm"
              data-testid={`trending-badge-${p.id}`}
              title={`Très consulté cette semaine (${p.views_7d || 0} vues)`}
            >
              <Flame size={11} strokeWidth={2} fill="currentColor" />
              Tendance
            </div>
          )}
          {p.status && p.status !== "Disponible" && (
            <div className="bg-[#1F2A36] text-white px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase font-semibold rounded-full shadow-sm">
              {p.status}
            </div>
          )}
        </div>
        <div className="absolute top-4 right-4 bg-[#1A1A1A] text-white w-8 h-8 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300 z-10">
          <ArrowUpRight size={16} strokeWidth={1.5} />
        </div>
      </div>

      {/* Text block below the image */}
      <div className="pt-4 flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[#6B6B6B]">
            <MapPin size={11} strokeWidth={1.5} />
            {p.location}
          </div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#A3805B] font-semibold">
            {p.type}
          </div>
        </div>
        <h3 className="font-serif text-xl leading-tight line-clamp-2 min-h-[2.5rem]">{p.title}</h3>
        <div className="flex items-center justify-between mt-1.5">
          <div className="font-serif text-xl text-[#A3805B]">{fmtPrice(p.price, p.transaction)}</div>
          {showSpecs && (
            <div className="flex items-center gap-3 text-[11px] text-[#6B6B6B]">
              <span className="flex items-center gap-1"><BedDouble size={12} strokeWidth={1.5} /> {p.bedrooms}</span>
              <span className="flex items-center gap-1"><Bath size={12} strokeWidth={1.5} /> {p.bathrooms}</span>
              <span className="flex items-center gap-1"><Maximize2 size={12} strokeWidth={1.5} /> {p.surface_weighted || p.surface_net || p.surface || 0}m²</span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); requestVisit(p); }}
          className="mt-3 w-full border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white py-2 text-[10.5px] tracking-[0.22em] uppercase font-semibold flex items-center justify-center gap-2 transition-colors duration-300 rounded-full"
          data-testid={`request-visit-${p.id}`}
          aria-label={`${t ? t("properties.request_visit") : "Demander une visite"} — ${p.title}`}
        >
          <CalendarCheck2 size={13} strokeWidth={1.8} />
          {t ? t("properties.request_visit") : "Demander une visite"}
        </button>
      </div>
    </article>
  );
}
